import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalized,
  type RouteLocationNormalizedLoaded,
  type RouteRecordRaw,
} from 'vue-router';
import routesConstants from '@/constants/routesConstants.ts';
import ImplicitCallback from '@/components/routing/ImplicitCallback.vue';
import ManageRedirection from '@/components/routing/ManageRedirection.vue';
import { useAuth } from '@okta/okta-vue';
import useUserStore from '@/stores/userStore.ts';
import technicalMessagesConstants from '@/constants/technicalMessagesConstants.ts';
import useErrorsStore from '@/stores/errorStore.ts';

const routes: RouteRecordRaw[] = [
  {
    path: routesConstants.AUTHENTICATION.path,
    component: ImplicitCallback,
  },
  {
    path: routesConstants.EXECUTIVE.path,
    name: routesConstants.EXECUTIVE.name,
    component: () => import('@/pages/ExecutivePage.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: routesConstants.MANAGER.path,
    name: routesConstants.MANAGER.name,
    component: () => import('@/pages/ManagerPage.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: routesConstants.HR.path,
    name: routesConstants.HR.name,
    component: () => import('@/pages/HrPage.vue'),
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/',
    component: ManageRedirection,
    meta: {
      requiresAuth: true,
    },
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [...routes],
});

router.beforeEach(customAuthRedirectGuard());

function customAuthRedirectGuard() {
  return async (to: RouteLocationNormalized, from: RouteLocationNormalizedLoaded, next: NavigationGuardNext) => {
    const auth = useAuth();
    const userStore = useUserStore();
    const errorStore = useErrorsStore();

    if (to.matched.some((record) => record.meta.requiresAuth) && !(await auth.isAuthenticated())) {
      await auth.signInWithRedirect({ originalUri: to.fullPath });
    } else if (await auth.isAuthenticated()) {
      await userStore.refreshUserInfos();

      if (
        (to.name === routesConstants.EXECUTIVE.name && !userStore.user.roles.isExec) ||
        (to.name === routesConstants.MANAGER.name && !userStore.user.roles.isManager) ||
        (to.name === routesConstants.HR.name && !userStore.user.roles.isHR)
      ) {
        errorStore.addFatalError({
          name: technicalMessagesConstants.HTTP._403.TITLE,
          message: technicalMessagesConstants.HTTP._403.MESSAGE,
        });

        next();
      } else {
        next();
      }
    } else {
      next();
    }
  };
}

export default router;
